import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Bell, BellRing } from 'lucide-react';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function EnableNotificationsButton({ color = '#00FF41', label = 'Enable Notifications' }) {
  const [status, setStatus] = useState('idle'); // idle | active | denied | busy | unsupported

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setStatus('unsupported');
      return;
    }
    if (Notification.permission === 'denied') { setStatus('denied'); return; }
    // Check existing subscription
    navigator.serviceWorker.getRegistration('/sw.js').then(reg => {
      if (!reg) return;
      reg.pushManager.getSubscription().then(sub => {
        if (sub) setStatus('active');
      });
    }).catch(() => {});
  }, []);

  const handleEnable = async () => {
    setStatus('busy');
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setStatus('denied');
        toast.error('Notifications blocked in browser settings');
        return;
      }
      const reg = await navigator.serviceWorker.register('/sw.js');
      const keyRes = await axios.get(`${API}/notifications/vapid-key`);
      const publicKey = keyRes.data.public_key;

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      let location = {};
      try {
        await new Promise((resolve) => {
          if (!navigator.geolocation) return resolve();
          navigator.geolocation.getCurrentPosition(
            pos => { location = { lat: pos.coords.latitude, lng: pos.coords.longitude }; resolve(); },
            () => resolve(),
            { timeout: 4000 }
          );
        });
      } catch { /* ignore */ }

      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/subscribe`, {
        subscription: sub.toJSON(),
        location,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setStatus('active');
      toast.success('🔔 Notifications enabled');
    } catch (err) {
      console.error(err);
      setStatus('idle');
      toast.error('Failed to enable notifications');
    }
  };

  if (status === 'unsupported') return null;

  const isActive = status === 'active';
  const isDenied = status === 'denied';

  return (
    <Button
      data-testid="enable-notifications-btn"
      onClick={isActive || isDenied ? undefined : handleEnable}
      disabled={status === 'busy' || isDenied}
      variant="outline"
      className="font-mono text-xs"
      style={{
        borderColor: color,
        color,
        background: isActive ? `${color}22` : 'transparent',
        boxShadow: `0 0 10px ${color}66`,
      }}
    >
      {isActive
        ? <><BellRing className="w-4 h-4 mr-2" /> ✅ Notifications Active</>
        : isDenied
        ? 'Notifications Blocked'
        : <><Bell className="w-4 h-4 mr-2" /> 🔔 {label}</>}
    </Button>
  );
}
